import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Button, Input, Modal, Progress } from 'antd';
import { useTranslation } from '../../contexts/LanguageContext';
import { useThreadsInsights } from '../../contexts/ThreadsInsightsContext';
import { useSelectedAccount } from '../../contexts/SelectedAccountContext';
import { AccountDropdown, useToast } from '../../components/ui';
import apiClient from '../../services/api/apiClient';
import styles from './DashboardInsights.module.css';

const { TextArea } = Input;

// Skeleton Loading Component
const SkeletonNumber = () => (
  <div className={styles.skeletonNumber} />
);

function DashboardInsights() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast, confirm } = useToast();
  const { selectedAccountId, setSelectedAccountId, accounts } = useSelectedAccount();
  const {
    getAccountInsights,
    fetchAccountInsights,
    isAccountLoading,
    isAccountFetching
  } = useThreadsInsights();

  const [selectedTimeRange, setSelectedTimeRange] = useState('today');
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [allReplies, setAllReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [displayedRepliesCount, setDisplayedRepliesCount] = useState(20);
  const [currentUsername, setCurrentUsername] = useState(null);

  // Automation stats
  const [automationTimeRange, setAutomationTimeRange] = useState('last7days');
  const [automationStats, setAutomationStats] = useState({
    postsPublished: { automated: 0, total: 0 },
    commentsReplied: { automated: 0, total: 0 },
    contentGenerated: { automated: 0, total: 0 },
    scheduledPosts: { automated: 0, total: 0 }
  });

  // Reply input
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [loadingAiSuggestion, setLoadingAiSuggestion] = useState(null);

  // Batch reply
  const [batchReplying, setBatchReplying] = useState(false);
  const [batchPaused, setBatchPaused] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [batchSuggestions, setBatchSuggestions] = useState({});
  const [batchSending, setBatchSending] = useState(false);
  const batchCancelledRef = useRef(false);
  const batchPausedRef = useRef(false);

  // Unified reply
  const [showUnifiedReplyModal, setShowUnifiedReplyModal] = useState(false);
  const [unifiedReplyText, setUnifiedReplyText] = useState('');
  const [unifiedReplyFilter, setUnifiedReplyFilter] = useState('');
  const [sendingUnifiedReply, setSendingUnifiedReply] = useState(false);
  const [unifiedReplyProgress, setUnifiedReplyProgress] = useState({ current: 0, total: 0 });

  // Filter only active Threads accounts with valid token for the dropdown
  const threadsAccounts = accounts.filter(acc =>
    acc.platform === 'threads' &&
    acc.status === 'active' &&
    acc.access_token
  );

  // Get current account insights
  const accountInsights = selectedAccountId ? getAccountInsights(selectedAccountId, selectedTimeRange) : null;
  const loadingInsights = selectedAccountId ? isAccountLoading(selectedAccountId, selectedTimeRange) : false;
  const fetchingInsights = selectedAccountId ? isAccountFetching(selectedAccountId, selectedTimeRange) : false;
  const isLoadingOrFetching = loadingInsights || fetchingInsights;

  // Load insights when selected account or time range changes
  useEffect(() => {
    if (selectedAccountId && accounts.length > 0) {
      const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
      if (selectedAccount && selectedAccount.platform === 'threads') {
        setLoadingPosts(true);
        fetchAccountInsights(selectedAccountId, false, selectedTimeRange);
      }
    }
  }, [selectedAccountId, selectedTimeRange, accounts]);

  // Load automation stats
  useEffect(() => {
    fetchAutomationStats();
  }, [automationTimeRange]);

  const fetchAutomationStats = async () => {
    try {
      const response = await apiClient.get(`/automation-stats?timeRange=${automationTimeRange}`);
      if (response.data.success) {
        setAutomationStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch automation stats:', error);
    }
  };

  // Load posts from accountInsights.recent_posts (returned by user-insights API)
  useEffect(() => {
    if (accountInsights?.recent_posts) {
      setCurrentUsername(accountInsights.account_name);
      const threadsPosts = accountInsights.recent_posts
        .map(post => ({
          id: post.id,
          content: post.text || '無內容',
          createdAt: post.timestamp,
          likeCount: post.likes_count ?? 0,
          replyCount: post.replies_count ?? 0,
          repostCount: post.reposts_count ?? 0,
          quoteCount: post.quotes_count ?? 0,
          views: post.views ?? 0,
          permalink: post.permalink
        }))
        .slice(0, 20);
      setPosts(threadsPosts);
      setLoadingPosts(false);
    }
  }, [accountInsights]);

  // Handle post click to load replies
  const handlePostClick = async (post) => {
    if (selectedPost && selectedPost.id === post.id) {
      setSelectedPost(null);
      setReplies([]);
      setAllReplies([]);
      return;
    }

    setSelectedPost(post);
    setLoadingReplies(true);
    setReplyingToId(null);
    setReplyText('');
    setBatchSuggestions({});

    try {
      const response = await apiClient.get(`/threads/insights/post-replies/${post.id}?accountId=${selectedAccountId}`);

      if (response.data.success && response.data.data) {
        setAllReplies(response.data.data);
        setReplies(response.data.data.slice(0, displayedRepliesCount));
      }
    } catch (err) {
      console.error('Failed to load replies:', err);
      toast.error('Failed to load comments');
    } finally {
      setLoadingReplies(false);
    }
  };

  // Load all replies
  const handleLoadAllReplies = () => {
    setReplies(allReplies);
    setDisplayedRepliesCount(allReplies.length);
  };

  // Send single reply
  const handleSendReply = async (replyToId) => {
    if (!replyText.trim()) return;

    setSendingReply(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/threads/insights/send-reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accountId: selectedAccountId,
          replyToId: replyToId,
          text: replyText
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Reply sent successfully!');
        handlePostClick(selectedPost);
        setReplyText('');
        setReplyingToId(null);
      } else {
        toast.error(data.message || 'Failed to send reply');
      }
    } catch (err) {
      console.error('Failed to send reply:', err);
      toast.error('Error sending reply');
    } finally {
      setSendingReply(false);
    }
  };

  // AI suggestion for single reply
  const handleAiSuggestion = async (reply) => {
    setReplyingToId(reply.id);
    setReplyText('');
    setLoadingAiSuggestion(reply.id);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/threads/insights/suggest-reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          postContent: selectedPost.content,
          replyText: reply.text,
          replyUsername: reply.username
        })
      });

      const data = await response.json();

      if (data.success && data.data.suggestion) {
        setReplyText(data.data.suggestion);
      } else {
        toast.error('Failed to get AI suggestion');
        setReplyingToId(null);
      }
    } catch (err) {
      console.error('Failed to get AI suggestion:', err);
      toast.error('Error getting AI suggestion');
      setReplyingToId(null);
    } finally {
      setLoadingAiSuggestion(null);
    }
  };

  // Batch AI reply
  const handleBatchAiReply = async () => {
    if (batchReplying) return;
    if (!selectedPost || replies.length === 0) return;

    const filteredReplies = replies.filter(reply => reply.username !== currentUsername && !reply.replied_by_me);

    if (filteredReplies.length === 0) {
      toast.warning('No comments to reply to');
      return;
    }

    const suggestions = { ...batchSuggestions };
    const repliesToProcess = filteredReplies.filter(reply => !suggestions[reply.id]);

    if (repliesToProcess.length === 0) {
      toast.info('All comments already have AI suggestions');
      return;
    }

    batchCancelledRef.current = false;
    batchPausedRef.current = false;
    setBatchReplying(true);
    setBatchPaused(false);
    setBatchProgress({ current: 0, total: repliesToProcess.length });

    const token = localStorage.getItem('token');
    let successCount = Object.keys(suggestions).length;

    try {
      for (let i = 0; i < repliesToProcess.length; i++) {
        const reply = repliesToProcess[i];

        while (batchPausedRef.current) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (batchCancelledRef.current) {
          break;
        }

        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/threads/insights/suggest-reply`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              postContent: selectedPost.content,
              replyText: reply.text,
              replyUsername: reply.username
            })
          });

          const data = await response.json();

          if (data.success && data.data.suggestion) {
            suggestions[reply.id] = data.data.suggestion;
            successCount++;
            setBatchSuggestions({ ...suggestions });
            setBatchProgress({ current: i + 1, total: repliesToProcess.length });
          } else {
            setBatchProgress({ current: i + 1, total: repliesToProcess.length });
          }

          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (err) {
          console.error(`Failed to get AI suggestion for ${reply.id}:`, err);
          setBatchProgress({ current: i + 1, total: repliesToProcess.length });
        }
      }

      if (!batchCancelledRef.current) {
        toast.success(`Successfully generated ${successCount} AI reply suggestions`);
      }
    } catch (err) {
      console.error('Batch AI reply failed:', err);
      toast.error('Error generating batch replies');
    } finally {
      setBatchReplying(false);
      setBatchPaused(false);
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  // Pause/Resume batch
  const handlePauseBatch = () => {
    batchPausedRef.current = !batchPausedRef.current;
    setBatchPaused(!batchPaused);
  };

  // Cancel batch
  const handleCancelBatch = () => {
    batchCancelledRef.current = true;
    setBatchReplying(false);
    setBatchPaused(false);
    setBatchProgress({ current: 0, total: 0 });
  };

  // Batch send all replies
  const handleBatchSend = async () => {
    if (Object.keys(batchSuggestions).length === 0) {
      toast.warning('No replies to send');
      return;
    }

    setBatchSending(true);
    const token = localStorage.getItem('token');
    let sentCount = 0;
    const totalCount = Object.keys(batchSuggestions).length;

    try {
      for (const [replyId, suggestion] of Object.entries(batchSuggestions)) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/threads/insights/send-reply`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              accountId: selectedAccountId,
              replyToId: replyId,
              text: suggestion
            })
          });

          const data = await response.json();
          if (data.success) {
            sentCount++;
          }

          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (err) {
          console.error(`Failed to send reply to ${replyId}:`, err);
        }
      }

      toast.success(`Successfully sent ${sentCount} / ${totalCount} replies`);
      handlePostClick(selectedPost);
      setBatchSuggestions({});
    } catch (err) {
      console.error('Failed to batch send replies:', err);
      toast.error('Error sending batch replies');
    } finally {
      setBatchSending(false);
    }
  };

  // Unified reply
  const handleUnifiedReply = async () => {
    if (!unifiedReplyText.trim()) {
      toast.warning('Please enter reply content');
      return;
    }

    // Filter replies: exclude own replies and already replied
    let filteredReplies = replies.filter(reply => reply.username !== currentUsername && !reply.replied_by_me);

    // If filter keyword is provided, only include replies containing the keyword
    if (unifiedReplyFilter.trim()) {
      const filterKeyword = unifiedReplyFilter.trim().toLowerCase();
      filteredReplies = filteredReplies.filter(reply =>
        reply.text && reply.text.toLowerCase().includes(filterKeyword)
      );
    }

    if (filteredReplies.length === 0) {
      toast.warning(unifiedReplyFilter.trim()
        ? `No comments containing "${unifiedReplyFilter.trim()}" found`
        : 'No comments to reply to'
      );
      return;
    }

    const filterInfo = unifiedReplyFilter.trim()
      ? ` (filtered by "${unifiedReplyFilter.trim()}")`
      : '';
    const confirmed = await confirm({
      title: 'Unified Reply',
      message: `Are you sure you want to reply to ${filteredReplies.length} comments${filterInfo} with the same content?`,
      confirmText: 'Yes, Reply',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    setSendingUnifiedReply(true);
    setUnifiedReplyProgress({ current: 0, total: filteredReplies.length });
    const token = localStorage.getItem('token');
    let sentCount = 0;

    try {
      for (let i = 0; i < filteredReplies.length; i++) {
        const reply = filteredReplies[i];
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/threads/insights/send-reply`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              accountId: selectedAccountId,
              replyToId: reply.id,
              text: unifiedReplyText.trim()
            })
          });

          const data = await response.json();
          if (data.success) {
            sentCount++;
          }

          setUnifiedReplyProgress({ current: i + 1, total: filteredReplies.length });
          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (err) {
          console.error(`Failed to send unified reply to ${reply.id}:`, err);
          setUnifiedReplyProgress({ current: i + 1, total: filteredReplies.length });
        }
      }

      toast.success(`Successfully sent ${sentCount} / ${filteredReplies.length} replies`);
      setShowUnifiedReplyModal(false);
      setUnifiedReplyText('');
      setUnifiedReplyFilter('');
      setUnifiedReplyProgress({ current: 0, total: 0 });
      handlePostClick(selectedPost);
    } catch (err) {
      console.error('Failed to send unified replies:', err);
      toast.error('Error sending unified replies');
    } finally {
      setSendingUnifiedReply(false);
      setUnifiedReplyProgress({ current: 0, total: 0 });
    }
  };

  // Render nested replies
  const renderNestedReply = (reply, depth = 0) => {
    const isReplying = replyingToId === reply.id;
    const hasBatchSuggestion = batchSuggestions[reply.id];

    return (
      <div key={reply.id} className="ml-8">
        <div className={styles.replyCard}>
          <div className={styles.replyHeader}>
            <div className={styles.replyUser}>
              <strong>@{reply.username}</strong>
              {reply.replied_by_me && (
                <span className={styles.repliedBadge}>
                  Replied
                </span>
              )}
            </div>
            <span className={styles.replyTime}>
              {new Date(reply.timestamp).toLocaleDateString('en-US')}
            </span>
          </div>

          <div className={styles.replyText}>
            {reply.text}
          </div>

          {hasBatchSuggestion && (
            <div className={styles.aiSuggestion}>
              <div className={styles.aiSuggestionHeader}>
                ✨ AI Suggested Reply:
              </div>
              <div className={styles.aiSuggestionText}>
                {batchSuggestions[reply.id]}
              </div>
            </div>
          )}

          {!reply.replied_by_me && reply.username !== currentUsername && (
            <div className={styles.replyActions}>
              <Button
                size="small"
                icon={<Icon icon="mdi:robot" width="16" />}
                onClick={() => handleAiSuggestion(reply)}
                loading={loadingAiSuggestion === reply.id}
              >
                AI Suggest
              </Button>
              <Button
                size="small"
                icon={<Icon icon="mdi:reply" width="16" />}
                onClick={() => {
                  setReplyingToId(reply.id);
                  setReplyText('');
                }}
              >
                Reply
              </Button>
            </div>
          )}

          {isReplying && (
            <div className={styles.replyInput}>
              <TextArea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Enter reply content..."
                rows={3}
                className="mb-2"
              />
              <div className="flex gap-2">
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleSendReply(reply.id)}
                  loading={sendingReply}
                >
                  Send
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setReplyingToId(null);
                    setReplyText('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className={styles.replyStats}>
            <span>
              <Icon icon="mdi:heart" width="14" />
              {reply.like_count || 0}
            </span>
          </div>
        </div>

        {reply.children && reply.children.length > 0 && (
          <div>
            {reply.children.map(child => renderNestedReply(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Connect Threads account
  const handleConnectThreads = async () => {
    try {
      const response = await apiClient.post('/user/social-accounts/pre-create', {
        platform: 'threads',
        contentType: 'mixed'
      });

      if (response.data.success) {
        const socialAccountId = response.data.accountId;

        // Use Cloud Run backend for OAuth callback
        const redirectUri = process.env.REACT_APP_THREADS_REDIRECT_URI ||
          'https://viealarc.app.n8n.cloud/webhook/38e45051-2282-4106-b05d-3a371109c735';

        const threadsOAuthUrl = 'https://threads.net/oauth/authorize' +
          '?client_id=1198067448800544' +
          `&redirect_uri=${encodeURIComponent(redirectUri)}` +
          '&scope=threads_basic,threads_content_publish,threads_read_replies,threads_manage_insights,threads_manage_replies,threads_keyword_search' +
          '&response_type=code' +
          '&auth_type=rerequest' +
          `&state=${socialAccountId}`;

        window.location.href = threadsOAuthUrl;
      }
    } catch (error) {
      console.error('Error connecting Threads:', error);
      toast.error('Failed to connect, please try again');
    }
  };

  // Empty states
  if (!selectedAccountId || accounts.length === 0) {
    return (
      <div className={styles.emptyCard}>
        <div className={styles.emptyIcon}>
          <Icon icon="mdi:account-alert" />
        </div>
        <h3 className={styles.emptyTitle}>
          No Account Connected
        </h3>
        <p className={styles.emptyDescription}>
          Please add an account from the sidebar to view insights
        </p>
      </div>
    );
  }

  const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
  if (selectedAccount && selectedAccount.platform !== 'threads') {
    return (
      <div className={styles.emptyCard}>
        <div className={styles.emptyIcon}>
          <Icon icon="mdi:information" />
        </div>
        <h3 className={styles.emptyTitle}>
          Threads Insights Only
        </h3>
        <p className={styles.emptyDescription}>
          Analytics are currently only available for Threads accounts
        </p>
      </div>
    );
  }

  // Time range options
  const timeRanges = [
    { value: 'today', label: 'Today' },
    { value: '7days', label: '7 Days' },
    { value: '30days', label: '30 Days' },
  ];

  // Stats configuration
  const stats = [
    {
      label: 'Total Posts',
      value: accountInsights?.insights?.total_posts || "-",
      icon: 'mdi:post',
      color: '#3b82f6'
    },
    {
      label: 'Profile Views',
      value: accountInsights?.insights?.profile_views || "-",
      icon: 'mdi:eye',
      color: '#3b82f6'
    },
    {
      label: 'Likes',
      value: accountInsights?.insights?.likes_count || "-",
      icon: 'mdi:heart',
      color: '#3b82f6'
    },
    {
      label: 'Replies',
      value: accountInsights?.insights?.replies_count || "-",
      icon: 'mdi:reply',
      color: '#3b82f6'
    },
    {
      label: 'Reposts',
      value: accountInsights?.insights?.reposts_count || "-",
      icon: 'mdi:repeat',
      color: '#3b82f6'
    },
    {
      label: 'Quotes',
      value: accountInsights?.insights?.quotes_count || "-",
      icon: 'mdi:format-quote-close',
      color: '#3b82f6'
    },
  ];

  return (
    <div className={styles.container}>
      {/* Time Range and Account Selector */}
      <div className={styles.controlsSection}>
        {/* Time Range Selector */}
        <div className={styles.timeRangeGroup}>
          <div className={styles.timeRangeLabel}>
            Time Range
          </div>
          <div className={styles.timeRangeButtons}>
            {timeRanges.map((timeRange) => (
              <button
                key={timeRange.value}
                onClick={() => setSelectedTimeRange(timeRange.value)}
                className={`${styles.timeRangeButton} ${selectedTimeRange === timeRange.value ? styles.active : ''}`}
              >
                {timeRange.label}
              </button>
            ))}
          </div>
        </div>

      </div>

      {/* Account Header */}
      <div className={styles.accountCard}>
        <div className={styles.accountInfo}>
          <div className={styles.accountHeader}>
            <h2 className={styles.accountName}>
              @{accountInsights?.account_name || selectedAccount?.account_name || 'Account'}
            </h2>
            {accountInsights?.is_verified && (
              <Icon icon="material-symbols:verified" className="text-base text-blue-500" />
            )}
          </div>
          <div className={styles.accountFollowers}>
            {isLoadingOrFetching ? (
              'Loading...'
            ) : accountInsights ? (
              accountInsights.insights?.followers_count
                ? `${accountInsights.insights.followers_count.toLocaleString()} followers`
                : '- followers'
            ) : '- followers'}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        {stats.map((stat, index) => (
          <div key={index} className={styles.statCard}>
            <div className={styles.statValue}>
              {isLoadingOrFetching ? (
                <SkeletonNumber />
              ) : (
                <>
                  <Icon
                    icon={stat.icon}
                    className="text-2xl text-blue-500 mr-2"
                  />
                  {stat.value.toLocaleString()}
                </>
              )}
            </div>
            <div className={styles.statLabel}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Automated Activity Section */}
      <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 mt-6">
        {/* Header with Time Range Selector */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-xl font-bold text-gray-900">Automated Activity</h2>
            </div>
          </div>

          {/* Time Range Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setAutomationTimeRange('yesterday')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                automationTimeRange === 'yesterday'
                  ? 'text-gray-900 bg-gray-100'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              Yesterday
            </button>
            <button
              onClick={() => setAutomationTimeRange('last7days')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                automationTimeRange === 'last7days'
                  ? 'text-gray-900 bg-gray-100'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              Last 7 days
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Posts Published */}
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900 mb-1">
              {automationStats.postsPublished.automated}
            </div>
            <h3 className="text-sm text-gray-600 mb-3">Posts Published</h3>
          </div>

          {/* Comments Replied */}
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900 mb-1">
              {automationStats.commentsReplied.automated}
            </div>
            <h3 className="text-sm text-gray-600 mb-3">Comments Replied</h3>
          </div>

          {/* Content Generated */}
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900 mb-1">
              {automationStats.contentGenerated.automated}
            </div>
            <h3 className="text-sm text-gray-600 mb-3">Content Generated</h3>
          </div>

          {/* Scheduled Posts */}
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900 mb-1">
              {automationStats.scheduledPosts.automated}
            </div>
            <h3 className="text-sm text-gray-600 mb-3">Scheduled Posts</h3>
          </div>
        </div>
      </div>

    </div>
  );
}

export default DashboardInsights;
